const BiotechUnit5Notes = `
<div class="space-y-8 animate-fadeIn">
    
    <!-- 1. Chapter Header -->
    <div>
        <h2 class="text-2xl font-black text-emerald-900 mb-3 border-b-2 border-slate-200 pb-2">Unit V: Fermentation Technology & Industrial Production</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            This final unit deals with the large-scale industrial cultivation of microbes (Fermentation) to produce commercially valuable products like antibiotics, vitamins, and organic acids. It also covers the collection and processing of human blood products.
        </p>
    </div>

    <!-- 2. Fermentation Basics -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-emerald-800 mb-4 border-b border-slate-100 pb-2">1. Fermentation Methods & General Requirements</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
            <!-- Methods -->
            <div class="bg-emerald-50 p-4 border border-emerald-100 rounded-lg">
                <h4 class="font-bold text-emerald-900 text-[15px] mb-2">A. Fermentation Methods</h4>
                <ul class="list-disc pl-4 text-[12px] text-slate-700 space-y-2">
                    <li><strong>Submerged Fermentation (SmF):</strong> Microbes are cultivated in a liquid nutrient broth. It allows precise control over temperature, pH, and oxygen. Used for mostly all industrial products (e.g., Penicillin).</li>
                    <li><strong>Solid State Fermentation (SSF):</strong> Microbes grow on a solid, moist substrate (like bran or grains) without free-flowing liquid. Mimics natural habitats of fungi. Used for specific enzymes and organic acids.</li>
                </ul>
            </div>

            <!-- Media & Sterilization -->
            <div class="bg-emerald-50 p-4 border border-emerald-100 rounded-lg">
                <h4 class="font-bold text-emerald-900 text-[15px] mb-2">B. Study of Media & Sterilization</h4>
                <ul class="list-disc pl-4 text-[12px] text-slate-700 space-y-2">
                    <li><strong>Fermentation Media:</strong> Must provide a Carbon source (Molasses, Corn steep liquor), Nitrogen source (Ammonia, Peptone), Minerals (P, S, Mg), and Vitamins to support massive microbial growth.</li>
                    <li><strong>Sterilization Methods:</strong> The media and the fermenter must be strictly sterilized to prevent contamination. Usually done by <strong>Batch Sterilization</strong> (in-situ heating to 121°C for 20-30 mins) or <strong>Continuous Sterilization</strong> (using heat exchangers).</li>
                </ul>
            </div>
        </div>

        <!-- Aeration & Stirring -->
        <h4 class="text-[15px] font-bold text-emerald-900 mb-2">C. Aeration Process & Stirring (Agitation)</h4>
        <p class="text-[13px] text-slate-700 mb-3">Most industrial fermentations are highly aerobic. Oxygen has low solubility in water, so continuous aeration and vigorous stirring are required.</p>
        <div class="bg-slate-50 p-3 rounded border border-slate-200 text-[13px] text-slate-700 flex flex-col md:flex-row gap-4">
            <div class="flex-1">
                <p class="font-bold text-emerald-800">💨 Aeration (Sparging):</p>
                <p class="text-[12px] mt-1">Sterile air is forced into the bottom of the fermenter through a device called a <strong>Sparger</strong>. It breaks the air into tiny bubbles, maximizing the surface area for oxygen transfer.</p>
            </div>
            <div class="flex-1 border-t md:border-t-0 md:border-l border-slate-200 pt-3 md:pt-0 md:pl-4">
                <p class="font-bold text-emerald-800">🌀 Stirring (Agitation):</p>
                <p class="text-[12px] mt-1">Achieved using mechanical <strong>Impellers</strong> mounted on a central shaft. It uniformly mixes the nutrients, microbes, and oxygen, and prevents the cells from settling at the bottom.</p>
            </div>
        </div>
    </div>

    <!-- 3. Fermenter Design -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-emerald-800 mb-4 border-b border-slate-100 pb-2">2. Large Scale Production Fermenter Design & Controls</h3>
        <p class="text-[13px] text-slate-700 mb-4">A fermenter (Bioreactor) is a closed cylindrical vessel (usually made of highly polished Stainless Steel 316) designed to provide an optimal, controlled environment for microbial growth.</p>
        
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-5 text-[12px] text-slate-700">
            <div class="p-3 border border-slate-200 rounded shadow-sm bg-slate-50">
                <p class="font-bold text-emerald-900 mb-1">🌡️ Temperature Control</p>
                <p>Microbial metabolism generates heat. A <strong>Cooling Jacket</strong> or internal cooling coils circulate cold water to maintain the exact optimum temperature.</p>
            </div>
            <div class="p-3 border border-slate-200 rounded shadow-sm bg-slate-50">
                <p class="font-bold text-emerald-900 mb-1">🧪 pH Control</p>
                <p>Metabolites often change the broth's pH. Automated pH sensors detect this and pump sterile Acid or Alkali into the vessel to neutralize it.</p>
            </div>
            <div class="p-3 border border-slate-200 rounded shadow-sm bg-slate-50">
                <p class="font-bold text-emerald-900 mb-1">🫧 Foam Control</p>
                <p>High aeration and proteins cause severe foaming, which can block exhaust filters. A mechanical foam breaker or chemical <strong>Antifoaming agents</strong> (e.g., silicone oil) are used.</p>
            </div>
            <div class="p-3 border border-slate-200 rounded shadow-sm bg-slate-50">
                <p class="font-bold text-emerald-900 mb-1">🛡️ Baffles</p>
                <p>Flat metal strips attached to the inner wall. They prevent the liquid from creating a massive whirlpool (vortex) and significantly improve mixing efficiency.</p>
            </div>
        </div>
    </div>

    <!-- 4. Study of Production -->
    <div class="bg-teal-50 p-6 border border-teal-200 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-teal-900 mb-4 border-b border-teal-200 pb-2">3. Industrial Production of Specific Metabolites</h3>
        
        <div class="overflow-x-auto rounded-lg border border-teal-200">
            <table class="min-w-full bg-white text-[13px] text-left">
                <thead class="bg-teal-700 text-white">
                    <tr>
                        <th class="px-4 py-2.5 font-bold w-1/5">Product</th>
                        <th class="px-4 py-2.5 font-bold w-1/4">Microorganism</th>
                        <th class="px-4 py-2.5 font-bold w-1/4">Key Raw Materials / Media</th>
                        <th class="px-4 py-2.5 font-bold">Process Highlights & Use</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-teal-100 text-slate-700">
                    <tr>
                        <td class="px-4 py-3 font-bold">Penicillins</td>
                        <td class="px-4 py-3 italic text-teal-800">Penicillium chrysogenum</td>
                        <td class="px-4 py-3">Corn steep liquor (nitrogen), Lactose (carbon), <strong>Phenylacetic acid</strong> (precursor).</td>
                        <td class="px-4 py-3">Aerobic submerged fermentation. Extracted using solvent extraction. (Broad-spectrum antibiotic).</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold">Citric Acid</td>
                        <td class="px-4 py-3 italic text-teal-800">Aspergillus niger</td>
                        <td class="px-4 py-3">Molasses or sucrose. Requires <strong>Trace metal deficiency</strong> (iron/manganese).</td>
                        <td class="px-4 py-3">Solid state or submerged. Low pH (2-3) maximizes yield. Used as a preservative/flavor in pharma and food.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold">Vitamin B12 <br><span class="text-[10px] font-normal">(Cyanocobalamin)</span></td>
                        <td class="px-4 py-3 italic text-teal-800">Pseudomonas denitrificans <br>or <em>Propionibacterium</em></td>
                        <td class="px-4 py-3">Beet molasses, yeast extract, and addition of <strong>Cobalt</strong> and 5,6-dimethylbenzimidazole.</td>
                        <td class="px-4 py-3">Requires micro-aerophilic conditions. Intracellular product, so cells must be lysed to extract it. (Treats pernicious anemia).</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold">Glutamic Acid</td>
                        <td class="px-4 py-3 italic text-teal-800">Corynebacterium glutamicum</td>
                        <td class="px-4 py-3">Carbohydrate source. Crucial requirement: <strong>Biotin-deficient media</strong>.</td>
                        <td class="px-4 py-3">Biotin deficiency makes the cell membrane leaky, allowing glutamic acid to be secreted out. (Used to make MSG, flavor enhancer).</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold">Griseofulvin</td>
                        <td class="px-4 py-3 italic text-teal-800">Penicillium griseofulvum</td>
                        <td class="px-4 py-3">Corn steep liquor, glucose, and a chloride source (since the molecule contains chlorine).</td>
                        <td class="px-4 py-3">Extracted from the mycelium using solvents. (Oral systemic antifungal for severe dermatophyte infections).</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <!-- 5. Blood Products -->
    <div class="bg-rose-50 p-6 mt-6 border border-rose-200 rounded-xl shadow-sm">
        <h3 class="text-[18px] font-black text-rose-900 mb-3 border-b border-rose-200 pb-2">4. Blood Products</h3>
        <p class="text-[13px] text-slate-700 mb-4">Handling human blood requires strict aseptic techniques to prevent contamination and the spread of blood-borne diseases (HIV, Hepatitis).</p>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-[13px]">
            <div class="bg-white p-4 rounded border border-rose-100 shadow-sm">
                <h4 class="font-bold text-rose-800 mb-2">Whole Human Blood</h4>
                <p class="text-[12px] text-slate-600">Collected from healthy donors into sterile bags containing an anticoagulant like <strong>ACD (Acid Citrate Dextrose)</strong> or <strong>CPD (Citrate Phosphate Dextrose)</strong>. Stored strictly at <strong>2°C to 6°C</strong>. Shelf life is usually 21 to 35 days. Used for severe blood loss.</p>
            </div>
            
            <div class="bg-white p-4 rounded border border-rose-100 shadow-sm">
                <h4 class="font-bold text-rose-800 mb-2">Dried Human Plasma</h4>
                <p class="text-[12px] text-slate-600">Blood is centrifuged to remove RBCs, leaving plasma. The liquid plasma is frozen and dehydrated under vacuum (<strong>Lyophilization/Freeze-drying</strong>). It can be stored at room temperature for years and reconstituted with sterile water before IV infusion.</p>
            </div>

            <div class="bg-white p-4 rounded border border-rose-100 shadow-sm">
                <h4 class="font-bold text-rose-800 mb-2">Plasma Substitutes</h4>
                <p class="text-[12px] text-slate-600">Artificial volume expanders used in emergencies when plasma is unavailable. They maintain osmotic pressure. Must be non-toxic, non-immunogenic, and stable. <br><em>Examples:</em> <strong>Dextran</strong> (a bacterial polysaccharide) and <strong>PVP (Polyvinylpyrrolidone)</strong>.</p>
            </div>
        </div>
    </div>

</div>
`;

export default BiotechUnit5Notes;